import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';
import { InspectionListCode } from 'src/app/models/inspection/inspection-list-code.model';
import {
  RgsObjectCompleteListWithOptions,
  RgsObjectCompleteListWithOptionsDTO,
} from 'src/app/models/objects/rgs-object-complete-list-with-options.model';
import { RgsObject } from 'src/app/models/objects/rgs-object.model';
import { PricebookCode } from 'src/app/models/pricebook/pricebook-code.model';
import { StructureCode } from 'src/app/models/structure/structure-code.model';
import { RgsHttpErrorResponse } from 'src/app/shared/models/http-error.model';
import { ActionType, CommonState } from 'src/app/shared/services/common-state.service';
import { ArrayUtils } from 'src/app/shared/utils/array.utils';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class RgsObjectService extends CommonState<RgsObject> {
  private readonly http = inject(HttpClient);

  private readonly apiUrl = `${environment.springAppUrl}/rgsobject`;
  public characteristics$ = new BehaviorSubject<Map<string, Set<string>>>(new Map());

  protected override itemsOperators = [
    map((dto: RgsObjectCompleteListWithOptionsDTO) => {
      const rgsObjectCompleteListWithOptions = new RgsObjectCompleteListWithOptions(dto);
      this.characteristics$.next(rgsObjectCompleteListWithOptions.options);
      return ArrayUtils.ensureArray(rgsObjectCompleteListWithOptions.data);
    }),
  ];

  constructor() {
    super(RgsObject);
  }

  public setCharacteristics(characteristics: Map<string, Set<string>>): void {
    this.characteristics$.next(characteristics);
  }

  /**
   * Retrieves the list of available {@link InspectionListCode} entries
   * that can be used when creating a new inspection for the specified {@link RgsObject}.
   *
   * The result includes all definitive inspection list records that are eligible
   * for use, based on the following criteria:
   *
   * - Definitive inspection lists created by the owner of the RgsObject
   * - Definitive inspection lists created under the currently logged-in license
   *
   * @param rgsObjectId The ID of the {@link RgsObject}.
   * @returns An observable containing an array of {@link InspectionListCode} available entries.
   */
  public findAvailableInspectionListsInspectionCreation(rgsObjectId: string) {
    return this.http.get<InspectionListCode[]>(
      `${this.apiUrl}/${rgsObjectId}/available-inspection-lists/inspection-creation`,
    );
  }

  /**
   * Retrieves the list of available {@link InspectionListCode} entries
   * that can be used when creating a new task for the specified {@link RgsObject}
   * and {@link Company}.
   *
   * The result includes all definitive inspection list records that are eligible
   * for use, based on the following criteria:
   *
   * - Definitive inspection lists created by the owner of the {@link RgsObject}
   * - Definitive inspection lists created under the specified {@link Company}
   *
   * @param rgsObjectId The ID of the {@link RgsObject}.
   * @param companyId The ID of the {@link Company}.
   * @return An observable containing an array of {@link InspectionListCode} available entries.
   */
  public findAvailableInspectionListsTaskCreation(rgsObjectId: string, companyId: string) {
    return this.http.get<InspectionListCode[]>(
      `${this.apiUrl}/${rgsObjectId}/available-inspection-lists/task-creation/${companyId}`,
    );
  }

  /**
   * Retrieves the list of available {@link Structure} entries
   * that can be used when creating a new inspection/scenario for the specified {@link RgsObject}.
   *
   * @param rgsObjectId The ID of the {@link RgsObject}.
   * @returns An observable containing an array of {@link StructureCode} available entries.
   */
  public findAvailableStructures(rgsObjectId: string) {
    return this.http.get<StructureCode[]>(`${this.apiUrl}/${rgsObjectId}/available-structures`);
  }

  public changeDirector(rgsObjectId: string, newCompanyDirectorId: string) {
    const actionId = uuidv4();
    this._loadingAction$.next(true);
    this._action$.next({
      id: actionId,
      type: ActionType.UPDATE,
      status: 'pending',
    });

    this.http.put<RgsObject>(`${this.apiUrl}/${rgsObjectId}/changeDirector/${newCompanyDirectorId}`, {}).subscribe({
      next: (rgsObject: RgsObject) => {
        this._loadingItems$.next(false);
        this._action$.next({
          id: actionId,
          type: ActionType.UPDATE,
          status: 'success',
        });
        this._selected$.next(rgsObject);
      },
      error: (err: RgsHttpErrorResponse) => {
        this._loadingItems$.next(false);
        this._action$.next({
          id: actionId,
          type: ActionType.UPDATE,
          status: 'error',
          httpError: err.error,
        });
      },
    });
  }

  /**
   * Retrieves the list of available {@link PricebookCode} entries
   * that can be used when creating/editing an inspection/scenario for the specified {@link RgsObject}.
   *
   * @param rgsObjectId The ID of the {@link RgsObject}.
   * @param selectedPricebookId The ID of the selected pricebook code. (optional)
   * @returns An observable containing an array of {@link PricebookCode} available entries.
   */
  public findAvailablePricebookCodes(rgsObjectId: string, selectedPricebookId?: string) {
    let params = new HttpParams();

    if (selectedPricebookId) {
      params = params.set('selectedPricebookId', selectedPricebookId);
    }

    return this.http.get<PricebookCode[]>(`${this.apiUrl}/${rgsObjectId}/available-pricebooks`, { params });
  }
}
